import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { fetchVisiblePromoVideos } from '../../../services/promoVideoService';

const PromoVideoSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const data = await fetchVisiblePromoVideos();
        setVideos(data);
      } catch (err) {
        console.error('Error loading promo videos:', err);
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };
    loadVideos();
  }, []);

  const categoryColors = {
    'Promotion': 'bg-brand-gold/10 text-brand-gold',
    'Activité': 'bg-white/10 text-white/70',
    'Événement': 'bg-purple-500/10 text-purple-400',
    'Présentation': 'bg-blue-500/10 text-blue-400',
  };

  return (
    <div className="bg-[#111111] rounded-3xl p-8 shadow-2xl border-none flex flex-col h-full">
      
      {/* Section Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl bg-brand-gold/10 flex items-center justify-center shadow-inner">
              <Icon name="Play" size={14} className="text-brand-gold" strokeWidth={2.5} />
            </div>
            <span className="text-[10px] font-bold text-brand-gold uppercase tracking-widest">
              Vidéos & Activités
            </span>
          </div>
          <h3 className="text-2xl font-heading font-bold text-white tracking-tight leading-snug">
            Nos Promotions<br />
            <span className="text-brand-gold">& Événements</span>
          </h3>
        </div>
        <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center cursor-pointer hover:bg-white/10 transition-colors">
          <Icon name="Plus" size={16} className="text-white/50" strokeWidth={2.5} />
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex flex-col gap-4 flex-1">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="bg-white/5 rounded-2xl h-24 animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && videos?.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 py-12">
          <div className="w-14 h-14 rounded-full bg-brand-gold/10 flex items-center justify-center shadow-inner">
            <Icon name="Video" size={24} className="text-brand-gold" strokeWidth={2} />
          </div>
          <span className="text-sm text-white/30 font-light text-center">
            Aucune vidéo disponible pour le moment
          </span>
        </div>
      )}

      {/* Video Cards */}
      {!loading && videos?.length > 0 && (
        <div className="flex flex-col gap-3 flex-1">
          {videos?.map((video) => {
            const catClass = categoryColors?.[video?.category] || categoryColors?.['Activité'];
            const isHovered = hoveredCard === video?.id;
            
            return (
              <div
                key={video?.id}
                className={`rounded-2xl p-4 cursor-pointer transition-all duration-300 border-none group ${
                  isHovered ? 'bg-white/10 translate-x-2' : 'bg-white/5'
                }`}
                onMouseEnter={() => setHoveredCard(video?.id)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => setActiveVideo(activeVideo === video?.id ? null : video?.id)}
              >
                <div className="flex items-center gap-4">
                  
                  {/* Thumbnail */}
                  <div className="w-20 h-16 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0 relative overflow-hidden shadow-inner">
                    {video?.thumbnail_url ? (
                      <img
                        src={video?.thumbnail_url}
                        alt={video?.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isHovered ? 'bg-brand-gold/20' : 'bg-white/10'}`}>
                        <Icon name="Play" size={12} className={isHovered ? 'text-brand-gold' : 'text-white/50'} strokeWidth={2.5} />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-md ${catClass}`}>
                        {video?.category}
                      </span>
                      {video?.duration && (
                        <span className="text-[10px] font-bold text-white/30 tracking-widest">
                          {video?.duration}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm font-bold truncate transition-colors ${isHovered ? 'text-white' : 'text-white/80'}`}>
                      {video?.title}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className={`flex-shrink-0 transition-transform duration-300 ${isHovered ? 'translate-x-1' : 'translate-x-0'}`}>
                    <Icon name="ChevronRight" size={18} className={isHovered ? 'text-brand-gold' : 'text-white/20'} strokeWidth={2.5} />
                  </div>
                </div>

                {/* Expanded State */}
                {activeVideo === video?.id && (
                  <div className="mt-4 pt-4 border-t border-white/5 animate-fade-in">
                    {video?.description && (
                      <p className="text-xs font-light text-white/50 leading-relaxed mb-4">
                        {video?.description}
                      </p>
                    )}
                    
                    {/* Video player or placeholder */}
                    {video?.video_url ? (
                      <video
                        ref={videoRef}
                        src={video?.video_url}
                        controls
                        className="w-full h-40 rounded-xl bg-black object-cover shadow-inner"
                      />
                    ) : (
                      <div className="w-full h-40 rounded-xl bg-white/5 flex flex-col items-center justify-center gap-3 border border-white/5">
                        <div className="w-12 h-12 rounded-full bg-brand-gold/10 flex items-center justify-center shadow-inner">
                          <Icon name="Video" size={20} className="text-brand-gold" strokeWidth={2} />
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/30">
                          Vidéo disponible prochainement
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer CTA */}
      <div className="pt-6 mt-6 border-t border-white/5 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-widest text-white/30">
          Nouvelles vidéos chaque semaine
        </span>
        <button className="flex items-center gap-2 px-4 py-2 rounded-full border border-brand-gold/30 text-brand-gold text-xs font-bold hover:bg-brand-gold/10 transition-colors uppercase tracking-widest">
          <Icon name="PlayCircle" size={14} strokeWidth={2.5} />
          Voir tout
        </button>
      </div>
      
    </div>
  );
};

export default PromoVideoSection;